/**
 * ActiveContinuityCard — the seeker "In Motion" pipeline (Prompt 2).
 *
 * Renders in State 4 ("Active Market"). A restrained, at-a-glance list of the seeker's
 * ACTIVE opportunities (serious lifecycle relationships only), plus active conversations.
 * Uses the canonical job_applications lifecycle (Prompt 1) via applicationStage helpers —
 * one relationship per opportunity, source-aware vocabulary, no CRM/kanban, no event dump.
 *
 * Saved/favorited jobs are NOT applications and never appear here.
 */

import { Card } from '@/components/ui/card';
import { MessageSquare, RefreshCw, ChevronRight } from 'lucide-react';
import {
  stageLabel, sourceLabel, isReported, isActive,
  opportunityTitle, employerOrProvider, lastActivityAt,
} from '@/lib/applicationStage';

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return null; }
}

export function ActiveContinuityCard({
  conversationCount = 0,
  applications = [],
  navigate,
}) {
  const safeConv = Number.isFinite(conversationCount) ? conversationCount : 0;
  const activeApps = (Array.isArray(applications) ? applications : []).filter(isActive);

  if (safeConv <= 0 && activeApps.length === 0) return null;

  return (
    <Card
      className="p-6 border border-slate-200 shadow-sm rounded-2xl"
      data-testid="active-continuity-card"
    >
      <div className="flex gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-6 h-6 text-brand" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-slate-900 mb-3">In Motion</h3>

          {activeApps.length > 0 && (
            <div className="space-y-2 mb-2" data-testid="in-motion-applications">
              {activeApps.map((app) => {
                const src = sourceLabel(app);
                const when = fmtDate(lastActivityAt(app));
                const employer = employerOrProvider(app);
                return (
                  <button
                    key={app.application_id}
                    type="button"
                    onClick={() => navigate(`/applications/${app.application_id}`)}
                    data-testid={`in-motion-app-${app.application_id}`}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {opportunityTitle(app)}
                        </span>
                        {src && (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1 py-0.5 flex-shrink-0">
                            {src}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {employer && (
                          <span className="text-xs text-slate-500 truncate">{employer}</span>
                        )}
                        {when && <span className="text-xs text-slate-400">· {when}</span>}
                      </div>
                    </div>
                    <span
                      className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-brand bg-brand-light rounded-full px-2.5 py-1"
                      data-testid={`in-motion-stage-${app.application_id}`}
                      title={isReported(app) ? 'You reported this' : undefined}
                    >
                      {stageLabel(app)}
                      {isReported(app) && (
                        <span className="text-[10px] text-brand/60 font-normal">· reported</span>
                      )}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {safeConv > 0 && (
            <button
              type="button"
              onClick={() => navigate('/messages')}
              data-testid="continuity-conversations-row"
              className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 transition-colors text-left"
            >
              <MessageSquare className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <span className="text-sm text-slate-700">
                {`${safeConv} active conversation${safeConv === 1 ? '' : 's'}`}
              </span>
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default ActiveContinuityCard;
